// ============================================================================
// 💬 CHATINPUT — Barra de Entrada de Mensaje (V3 — Upload + WhatsApp-Grade)
// src/features/mensagens/components/chat/ChatInput.jsx
// ============================================================================
// 🔥 V2: Selector de plantillas integrado · Editar antes de enviar
// 🔥 V3: Upload de archivos (imagen/docs) → Cloudinary → WhatsApp
// 🔥 V3: Preview de adjuntos · Barra de progreso · Validación integrada
// ============================================================================

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send,
  Paperclip,
  Mic,
  X,
  Smile,
  Image,
  FileText,
  Loader2,
  MessageSquare,
  FileCheck,
  Home,
  Bell,
  PlusSquare,
  TrendingDown,
  CheckCircle,
  MessageCircle,
  Cake,
  Star,
  Clock,
  Award,
} from 'lucide-react';
import { validateMensagem, validateAnexo } from '../../utils/validators';
import { PERFORMANCE, UI_DIMENSIONS } from '../../utils/constants';
// 🔥 V2: Importar catálogo oficial de plantillas
import {
  getTemplates,
  generateTemplateMessage,
} from '../../api/templates';
// 🔥 V3: Servicio de upload a Cloudinary
import { uploadToCloudinary } from '../../../../services/cloudinaryService';

// 🔥 V2: Mapa de íconos del catálogo a componentes Lucide
const ICON_MAP = {
  MessageSquare, FileText, Home, Bell, PlusSquare,
  TrendingDown, FileCheck, CheckCircle, MessageCircle, Cake,
  Star, Clock, Award,
};

const ChatInput = ({
  onSend,
  onAttach,
  placeholder = 'Digite uma mensagem...',
  disabled = false,
  loading = false,
  replyingTo = null,
  onCancelReply,
  // 🔥 V2: Nombre del cliente para personalizar plantillas
  clientName = '',
}) => {
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  // 🔥 V2: Estado para panel de plantillas
  const [showTemplates, setShowTemplates] = useState(false);
  const [validationError, setValidationError] = useState(null);
  
  // 🔥 V3: Estados de upload
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pendingAttachment, setPendingAttachment] = useState(null); // { type, file, name, size }
  
  const inputRef = useRef(null);
  const attachMenuRef = useRef(null);
  const templatesMenuRef = useRef(null);
  const fileInputRef = useRef(null); // 🔥 V3: Ref para input file oculto

  // 🔥 V2: Obtener plantillas del catálogo
  const templates = getTemplates();

  // Auto-resize
  const adjustHeight = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    const newHeight = Math.min(
      Math.max(input.scrollHeight, UI_DIMENSIONS.INPUT_MIN_HEIGHT),
      UI_DIMENSIONS.INPUT_MAX_HEIGHT
    );
    input.style.height = `${newHeight}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  // Cerrar menús al clickar fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target)) {
        setShowAttachMenu(false);
      }
      if (templatesMenuRef.current && !templatesMenuRef.current.contains(e.target)) {
        setShowTemplates(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 🔥 V2: Seleccionar plantilla — carga el texto en el input para editar
  const handleSelectTemplate = useCallback((templateId) => {
    const generatedText = generateTemplateMessage(templateId, { name: clientName });
    setValue(generatedText);
    setShowTemplates(false);
    // Enfocar el input para que el usuario pueda editar
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(generatedText.length, generatedText.length);
    }, 100);
  }, [clientName]);

  // 🔥 V3: Manejar selección de archivo → Validar → Subir a Cloudinary
  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    const type = e.target.getAttribute('data-type') || 'image';
    
    // Reset input para permitir re-seleccionar el mismo archivo
    e.target.value = '';
    
    if (!file) return;

    // Validar archivo con el validador existente
    const validation = validateAnexo(file, type);
    if (!validation.valid) {
      setValidationError(validation.error);
      // Auto-limpiar error después de 4 segundos
      setTimeout(() => setValidationError(null), 4000);
      return;
    }

    // Mostrar preview del archivo seleccionado
    setPendingAttachment({ type, file, name: file.name, size: file.size });
    setValidationError(null);
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      // Subir a Cloudinary usando el servicio existente
      const result = await uploadToCloudinary(
        file,
        'chat',                          // propertyId
        'mensagens',                     // userId  
        (progress) => setUploadProgress(progress)
      );

      // Actualizar pendingAttachment con la URL de Cloudinary
      setPendingAttachment(prev => ({
        ...prev,
        url: result.url,
        thumbnail: result.thumbnail,
        preview: result.preview,
        cloudinaryPublicId: result.publicId
      }));

      // Construir mensaje con attachment
      const messageData = {
        text: value.trim() || `📎 ${file.name}`,
        attachments: [{
          type,
          url: result.url,
          thumbnail: result.thumbnail,
          preview: result.preview,
          name: file.name,
          size: file.size,
          mimeType: file.type,
          cloudinaryPublicId: result.publicId
        }]
      };

      // Enviar mensaje con attachment
      onSend?.(messageData);
      
      // Limpiar estados
      setValue('');
      setPendingAttachment(null);
      setUploading(false);
      setUploadProgress(0);
      
      // Reset height del textarea
      if (inputRef.current) {
        inputRef.current.style.height = `${UI_DIMENSIONS.INPUT_MIN_HEIGHT}px`;
      }
      
    } catch (error) {
      console.error('❌ [ChatInput] Error upload:', error);
      setValidationError(error.message || 'Erro ao subir arquivo');
      setPendingAttachment(null);
      setUploading(false);
      setUploadProgress(0);
      // Auto-limpiar error después de 4 segundos
      setTimeout(() => setValidationError(null), 4000);
    }
  }, [value, onSend]);

  // 🔥 V3: Enviar mensaje (texto + posible attachment pendiente)
  const handleSend = useCallback(() => {
    // Si hay un attachment pendiente ya subido, enviar con texto
    if (pendingAttachment?.url) {
      const messageData = {
        text: value.trim() || `📎 ${pendingAttachment.name}`,
        attachments: [{
          type: pendingAttachment.type,
          url: pendingAttachment.url,
          thumbnail: pendingAttachment.thumbnail,
          preview: pendingAttachment.preview,
          name: pendingAttachment.name,
          size: pendingAttachment.size,
          mimeType: pendingAttachment.file?.type,
          cloudinaryPublicId: pendingAttachment.cloudinaryPublicId
        }]
      };
      onSend?.(messageData);
      setValue('');
      setPendingAttachment(null);
      
      // Reset height
      if (inputRef.current) {
        inputRef.current.style.height = `${UI_DIMENSIONS.INPUT_MIN_HEIGHT}px`;
      }
      
      // Refocus
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }

    // Envío normal de texto
    const { valid, sanitized, error } = validateMensagem(value);
    
    if (!valid) {
      setValidationError(error);
      // Auto-limpiar error después de 4 segundos
      setTimeout(() => setValidationError(null), 4000);
      return;
    }

    setValidationError(null);
    onSend?.(sanitized);
    setValue('');
    
    // Reset height
    if (inputRef.current) {
      inputRef.current.style.height = `${UI_DIMENSIONS.INPUT_MIN_HEIGHT}px`;
    }

    // Refocus
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [value, pendingAttachment, onSend]);

  // Enviar con Enter (Shift+Enter = nueva línea)
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // 🔥 V3: Abre selector de archivos según tipo (imagen/documento)
  const handleAttach = useCallback((type) => {
    setShowAttachMenu(false);
    
    if (!fileInputRef.current) return;
    
    // Configurar accept según tipo
    const limits = { image: { types: ["image/jpeg","image/png","image/webp"] }, document: { types: [".pdf",".doc",".docx",".txt"] } }[type];
    if (limits) {
      fileInputRef.current.accept = limits.types.join(',');
    }
    fileInputRef.current.setAttribute('data-type', type);
    fileInputRef.current.click();
  }, []);

  // 🔥 V3: Cancelar attachment pendiente
  const handleCancelAttachment = useCallback(() => {
    setPendingAttachment(null);
    setUploading(false);
    setUploadProgress(0);
  }, []);

  return (
    <div className="bg-card border-t border-gray-200 px-3 py-2">
      {/* 🔥 V3: Input file oculto para adjuntar archivos */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        aria-hidden="true"
      />

      {/* Mensaje respondiendo */}
      <AnimatePresence>
        {replyingTo && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="flex items-center gap-2 bg-blue-50 rounded-lg px-3 py-1.5 mb-2 border-l-4 border-blue-500">
              <span className="text-xs text-blue-600 flex-1 truncate">
                Respondendo: {replyingTo}
              </span>
              <button
                onClick={onCancelReply}
                className="p-0.5 rounded-full hover:bg-blue-100 text-blue-500"
              >
                <X size={14} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🔥 V3: Preview de archivo adjunto pendiente */}
      <AnimatePresence>
        {pendingAttachment && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden mb-2"
          >
            <div className={`flex items-center gap-2 rounded-lg px-3 py-2 border ${
              pendingAttachment.url 
                ? 'bg-green-50 border-green-200' 
                : 'bg-blue-50 border-blue-200'
            }`}>
              {pendingAttachment.type === 'image' ? (
                <Image size={18} className={pendingAttachment.url ? 'text-green-600' : 'text-blue-600'} />
              ) : (
                <FileText size={18} className={pendingAttachment.url ? 'text-green-600' : 'text-blue-600'} />
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-medium truncate ${
                  pendingAttachment.url ? 'text-green-700' : 'text-blue-700'
                }`}>
                  {pendingAttachment.name}
                </p>
                <p className={`text-[10px] ${
                  pendingAttachment.url ? 'text-green-500' : 'text-blue-500'
                }`}>
                  {Math.round(pendingAttachment.size / 1024)}KB
                  {uploading && ` · Subindo ${uploadProgress}%`}
                  {pendingAttachment.url && ' · ✅ Pronto'}
                </p>
              </div>
              {uploading ? (
                <div className="flex-shrink-0 w-6 h-6">
                  <svg className="animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="text-blue-200" />
                    <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="text-blue-600" />
                  </svg>
                </div>
              ) : (
                <button
                  onClick={handleCancelAttachment}
                  className="p-1 rounded-full hover:bg-red-100 text-gray-400 hover:text-red-500 flex-shrink-0"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🔥 V2: Panel de plantillas */}
      <AnimatePresence>
        {showTemplates && (
          <motion.div
            ref={templatesMenuRef}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden mb-2"
          >
            <div className="bg-gray-50 rounded-xl p-2 border border-gray-200">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Plantillas rápidas
                </span>
                <button
                  onClick={() => setShowTemplates(false)}
                  className="p-0.5 rounded-full hover:bg-gray-200 text-gray-400"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {templates.map((t) => {
                  const Icon = ICON_MAP[t.icon] || MessageSquare;
                  const categoryColors = {
                    atendimento: 'from-blue-500 to-indigo-500',
                    negociacao: 'from-green-500 to-emerald-500',
                    visitas: 'from-purple-500 to-pink-500',
                    followup: 'from-amber-500 to-orange-500',
                    imoveis: 'from-indigo-500 to-purple-500',
                    documentacao: 'from-teal-500 to-cyan-500',
                    pos_venda: 'from-cyan-500 to-blue-500',
                    relacionamento: 'from-pink-500 to-rose-500',
                  };
                  const gradient = categoryColors[t.category] || 'from-blue-500 to-indigo-500';
                  
                  return (
                    <button
                      key={t.id}
                      onClick={() => handleSelectTemplate(t.id)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-card transition-colors text-left group border border-transparent hover:border-gray-300 hover:shadow-sm"
                    >
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                        <Icon size={14} className="text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-700 truncate">{t.name}</p>
                        <p className="text-[10px] text-gray-400 truncate">{t.preview?.replace('{nome}', clientName || 'Cliente').slice(0, 40)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error de validación */}
      <AnimatePresence>
        {validationError && (
          <motion.p
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="text-xs text-red-500 mb-1 px-1"
          >
            {validationError}
          </motion.p>
        )}
      </AnimatePresence>

      {/* Input + Botones */}
      <div className="flex items-end gap-2">
        {/* 🔥 V2: Botón de plantillas */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => {
              setShowTemplates(!showTemplates);
              setShowAttachMenu(false);
            }}
            disabled={disabled}
            className={`
              p-2 rounded-full transition-colors
              ${disabled
                ? 'text-gray-300 cursor-not-allowed'
                : showTemplates
                  ? 'text-purple-600 bg-purple-50'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }
            `}
            aria-label="Plantillas"
            title="Plantillas rápidas"
          >
            <MessageSquare size={20} />
          </button>
        </div>

        {/* Botón adjuntar */}
        <div className="relative flex-shrink-0" ref={attachMenuRef}>
          <button
            onClick={() => {
              setShowAttachMenu(!showAttachMenu);
              setShowTemplates(false);
            }}
            disabled={disabled || uploading}
            className={`
              p-2 rounded-full transition-colors
              ${disabled || uploading
                ? 'text-gray-300 cursor-not-allowed'
                : showAttachMenu
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }
            `}
            aria-label="Anexar"
          >
            <Paperclip size={20} />
          </button>

          {/* Menú de adjuntos */}
          <AnimatePresence>
            {showAttachMenu && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                className="absolute bottom-full left-0 mb-2 bg-card rounded-xl shadow-xl border border-gray-200 p-1.5 z-20"
              >
                <button
                  onClick={() => handleAttach('image')}
                  className="flex items-center gap-2 w-full px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700 whitespace-nowrap"
                >
                  <Image size={16} className="text-green-500" />
                  Imagem
                </button>
                <button
                  onClick={() => handleAttach('document')}
                  className="flex items-center gap-2 w-full px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700 whitespace-nowrap"
                >
                  <FileText size={16} className="text-blue-500" />
                  Documento
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Input */}
        <div className={`
          flex-1 bg-gray-100 rounded-2xl px-4 py-2
          transition-all duration-200
          ${focused ? 'ring-2 ring-blue-300 bg-card' : ''}
          ${uploading ? 'ring-2 ring-green-300 bg-green-50' : ''}
        `}>
          <textarea
            ref={inputRef}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setValidationError(null);
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={handleKeyDown}
            placeholder={uploading ? 'Subindo arquivo...' : placeholder}
            disabled={disabled || uploading}
            rows={1}
            className="w-full bg-transparent text-sm text-gray-700 placeholder-gray-400
                       resize-none outline-none border-none
                       max-h-[120px] overflow-y-auto"
            style={{ minHeight: `${UI_DIMENSIONS.INPUT_MIN_HEIGHT}px` }}
          />
        </div>

        {/* Botón enviar / micrófono */}
        {(value.trim() || pendingAttachment?.url) ? (
          <button
            onClick={handleSend}
            disabled={disabled || loading || uploading}
            className={`
              p-2 rounded-full flex-shrink-0 transition-all
              ${disabled || loading || uploading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : pendingAttachment?.url
                  ? 'bg-green-500 text-white hover:bg-green-600 active:scale-95 shadow-lg shadow-green-200'
                  : 'bg-blue-500 text-white hover:bg-blue-600 active:scale-95 shadow-lg shadow-blue-200'
              }
            `}
            aria-label="Enviar"
          >
            {loading || uploading ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <Send size={20} />
            )}
          </button>
        ) : (
          <button
            disabled={disabled}
            className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex-shrink-0 transition-colors"
            aria-label="Gravar áudio"
          >
            <Mic size={20} />
          </button>
        )}
      </div>
    </div>
  );
};

ChatInput.displayName = 'ChatInput';
export { ChatInput };
export default ChatInput;